import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./HomecomingPaywall.module.scss"

/**
 * HomecomingPaywall (`/homecoming`) — a self-contained EXPIRATION / win-back paywall
 * for Private Photo Vault, built for the one situation none of the app's prior
 * expiration paywalls address: the member who has ALREADY lapsed and is coming
 * back.
 *
 * Divergence brief: claude-paywalls/_brief.md · ledger: claude-paywalls/_ledger.md
 *
 * The recon over the ledger found all 11 existing expiration concepts are really
 * *pre-lapse "don't let it stop"* renewals — a warm-continuity cluster (Unbroken,
 * Keep Posted, Patchwork, Evergreen, Beacon, Mainspring, Postmark, …) plus one fear
 * outlier (Deep Freeze). Nobody built the emotional moment of RETURNING after a gap,
 * and nobody made the mandatory no-data-loss guarantee the *spine* instead of a
 * footnote. Homecoming does exactly that. Every axis is chosen off the worn paths:
 *
 *   - Register:   REUNION / relief-of-return — "welcome back, nothing changed." Not
 *                 fear, not streak-pride, not seasonal-continuity. Unused.
 *   - Value:      "EXACTLY AS YOU LEFT IT." The lapsed user's real fear is "did I
 *                 lose anything while I was gone?" The answer — nothing was deleted,
 *                 moved, or downgraded — IS the concept, not a disclaimer. Turning
 *                 protection back on simply resumes from today.
 *   - Layout:     a preserved MEMORY WALL under a frosted "we kept these safe" cover
 *                 — an interactive reveal stage as the hero, a reunion greeting band
 *                 above it, and the plans as a separate lower module (deliberately
 *                 NOT fused into a whole-year fill, which the family over-used).
 *   - Interaction:LIFT THE COVER — drag the frosted pane up off the wall (or tap
 *                 "Lift the cover"). Latches once lifted. First peel/lift mechanic.
 *   - Motion:     honesty is ENCODED in the motion — the photos never fade in from
 *                 grey and never "come to life"; they are fully vivid the entire
 *                 time, because they were never lost. Only the cover moves, and a
 *                 warm "light of home" glow grows behind it. This is the deliberate
 *                 opposite of the grey→colour "bloom reveal" the ledger flags.
 *   - Art:        warm homecoming light. Amber (--color-secondary) = the warmth of
 *                 home / welcome — REASSIGNING the family's amber=at-risk convention;
 *                 brand blue = the vault that held steady.
 *
 * Honest & App Store compliant: the pitch is a real returning-user win-back for the
 * real Premium bundle (automatic full-resolution backup, private on-device
 * encryption, sync across your devices). It NEVER implies data is lost on
 * expiration — the entire screen says the opposite. No countdown, no fake scarcity;
 * the savings figure is exact yearly-vs-monthly arithmetic. The revealed wall is the
 * bundled demo library, labelled as a sample. Yearly is the featured, default plan
 * with the most prominent price; monthly is small and secondary.
 */

type PlanKey = "yearly" | "monthly"

// The preserved memory wall — bundled demo photos from public/byheart/ (400×400,
// no network egress). Read instantly as a personal camera roll. Weighted layout so
// the wall feels like a real hung gallery, not a uniform grid.
const WALL: { id: string; span: boolean }[] = [
  { id: "m03", span: true },
  { id: "m07", span: false },
  { id: "m11", span: false },
  { id: "m14", span: false },
  { id: "m02", span: false },
  { id: "m09", span: true },
  { id: "m05", span: false },
  { id: "m16", span: false },
  { id: "m12", span: false },
  { id: "m01", span: false },
  { id: "m18", span: true },
  { id: "m06", span: false },
]

const photo = (id: string) => `/byheart/${id}.jpg`

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

// ── inline glyphs (no new asset deps) ────────────────────────────────────────
const Shield = ({ size = 14 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M12 2.5l7 2.8v5.2c0 4.6-3 8.3-7 9.8-4-1.5-7-5.2-7-9.8V5.3l7-2.8z"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinejoin="round"
    />
    <path
      d="M8.6 12.2l2.3 2.3 4.4-4.6"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Tick = ({ size = 12 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// an open padlock — protection paused, ready to click shut again
const LockOpen = ({ size = 15 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <rect
      x={4.5}
      y={10.5}
      width={15}
      height={10}
      rx={2.4}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
    />
    <path
      d="M8 10.5V7.2A4 4 0 0 1 15.8 6"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
    />
    <circle cx={12} cy={15.2} r={1.5} fill="currentColor" />
  </svg>
)

const CloudSync = ({ size = 15 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M7 18h9.5a3.5 3.5 0 0 0 .4-6.98A5 5 0 0 0 7.2 10.2 4 4 0 0 0 7 18z"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinejoin="round"
    />
    <path
      d="M10.4 13.6l1.6-1.6 1.6 1.6M12 12v3.6"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Devices = ({ size = 15 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <rect
      x={3}
      y={5}
      width={13}
      height={9.5}
      rx={1.6}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
    />
    <path
      d="M6 17.5h7"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
    />
    <rect
      x={16.5}
      y={9}
      width={5}
      height={10}
      rx={1.4}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
    />
  </svg>
)

const ArrowUp = ({ size = 16 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M12 19V6M6 11l6-5 6 5"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const HomecomingPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── the cover ────────────────────────────────────────────────────────────
  // `lift` is the latched maximum the cover has been raised to (0 = fully covering,
  // 1 = lifted clear). `view` is what's rendered — during a drag it follows the
  // pointer; otherwise it rests on `lift`. Latches: you can't re-cover by dragging.
  const [lift, setLift] = useState(0)
  const [view, setView] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [hinted, setHinted] = useState(false)

  const stageRef = useRef<HTMLDivElement>(null)
  const liftRef = useRef(0)
  const dragStart = useRef<{ y: number; lift: number } | null>(null)
  const raf = useRef<number | null>(null)

  useEffect(() => {
    liftRef.current = lift
  }, [lift])

  const clearRaf = useCallback(() => {
    if (raf.current != null) {
      cancelAnimationFrame(raf.current)
      raf.current = null
    }
  }, [])
  useEffect(() => () => clearRaf(), [clearRaf])

  const revealed = lift >= 0.62
  const shown = dragging ? view : lift

  // teaching hint: the cover nudges up a little once on load, then settles back —
  // just enough to say "this lifts."
  useEffect(() => {
    if (reduceMotion) {
      setLift(1)
      setView(1)
      setHinted(true)
      return
    }
    let cancelled = false
    const start = window.setTimeout(() => {
      const t0 = performance.now()
      const dur = 1250
      const tick = (now: number) => {
        if (cancelled) return
        const k = clamp((now - t0) / dur, 0, 1)
        // ease up to ~0.14 then back to 0
        const eased =
          k < 0.55 ? (k / 0.55) * 0.14 : 0.14 * (1 - (k - 0.55) / 0.45)
        setView(eased)
        if (k < 1) raf.current = requestAnimationFrame(tick)
        else {
          setView(0)
          setHinted(true)
        }
      }
      raf.current = requestAnimationFrame(tick)
    }, 700)
    return () => {
      cancelled = true
      window.clearTimeout(start)
      if (raf.current) cancelAnimationFrame(raf.current)
    }
  }, [reduceMotion])

  const liftFromPointer = (clientY: number) => {
    const el = stageRef.current
    const start = dragStart.current
    if (!el || !start) return
    const h = el.getBoundingClientRect().height || 1
    // dragging upward (smaller clientY) raises the cover
    const next = clamp(start.lift + (start.y - clientY) / (h * 0.72), 0, 1)
    setView(next)
    setLift((p) => Math.max(p, next))
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (revealed) return
    setHinted(true)
    clearRaf()
    setDragging(true)
    dragStart.current = { y: e.clientY, lift: liftRef.current }
    setView(liftRef.current)
    stageRef.current?.setPointerCapture?.(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging) return
    liftFromPointer(e.clientY)
  }
  const endDrag = () => {
    if (!dragging) return
    setDragging(false)
    dragStart.current = null
    // if past a light threshold, snap open; otherwise settle back to latched lift
    if (liftRef.current >= 0.4) {
      animateLift(1)
    } else {
      setView(liftRef.current)
    }
  }

  const animateLift = (to: number) => {
    if (reduceMotion) {
      setLift((p) => Math.max(p, to))
      setView(to)
      return
    }
    clearRaf()
    const from = Math.max(liftRef.current, view)
    const t0 = performance.now()
    const dur = 950 * Math.max(0.2, to - from)
    const ease = (k: number) => 1 - Math.pow(1 - k, 3)
    const tick = (now: number) => {
      const k = clamp((now - t0) / Math.max(dur, 1), 0, 1)
      const v = from + (to - from) * ease(k)
      setView(v)
      setLift((p) => Math.max(p, v))
      if (k < 1) raf.current = requestAnimationFrame(tick)
    }
    raf.current = requestAnimationFrame(tick)
  }

  const liftCover = () => {
    if (revealed) return
    setHinted(true)
    native.logMixpanelEvent("html_homecoming_lift", {})
    animateLift(1)
  }

  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }
  const handleClose = () => native.postNativeEvent("close")
  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const ctaLabel = hasTrial
    ? `Turn protection back on — ${trialDays} days free`
    : "Turn protection back on"
  const ctaSub =
    plan === "yearly"
      ? hasTrial
        ? `${trialDays} days free, then ${yearlyPrice}/yr · resumes from today · cancel anytime`
        : `${yearlyPrice}/yr · resumes from today · cancel anytime`
      : `${monthlyPrice}/mo · resumes from today · cancel anytime`

  // cover-derived display values
  const glow = shown // warm "light of home" grows as the cover lifts
  const coverTranslate = -shown * 108 // % — slides up and clear of the stage

  return (
    <div className={styles.page}>
      <button className={styles.close} onClick={handleClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      {/* ── reunion greeting band ─────────────────────────────────────────── */}
      <header className={styles.head}>
        <div className={styles.brand}>
          <span className={styles.brandKey} aria-hidden>
            <Shield size={13} />
          </span>
          <span className={styles.brandName}>Private Photo Vault</span>
        </div>
        <span className={styles.backChip}>Welcome back</span>
        <h1 className={styles.title}>
          Your vault kept everything <em>exactly as you left it.</em>
        </h1>
        <p className={styles.sub}>
          Your Premium lapsed, so automatic backup is paused — but nothing was
          deleted, moved, or downgraded. Every photo and video is right where it
          was. Lift the cover and see for yourself.
        </p>
      </header>

      {/* ── the reveal stage: a preserved memory wall under a frosted cover ── */}
      <section
        className={styles.stageWrap}
        aria-label="Your preserved memories"
      >
        <div
          ref={stageRef}
          className={`${styles.stage} ${dragging ? styles.stageDrag : ""} ${
            revealed ? styles.stageOpen : ""
          }`}
          role="button"
          tabIndex={0}
          aria-label="Lift the cover to reveal your memories, safe and unchanged"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              liftCover()
            }
          }}
        >
          {/* warm light of home, growing behind the wall */}
          <span
            className={styles.homeGlow}
            style={{ opacity: 0.25 + glow * 0.75 }}
            aria-hidden
          />

          {/* the memories — always fully vivid; they were never lost */}
          <div className={styles.wall} aria-hidden>
            {WALL.map((w) => (
              <span
                key={w.id}
                className={`${styles.frame} ${w.span ? styles.frameWide : ""}`}
              >
                <img src={photo(w.id)} alt="" draggable={false} />
              </span>
            ))}
          </div>

          {/* the frosted "we kept these safe" cover that slides up and away */}
          <div
            className={styles.cover}
            style={{
              transform: `translateY(${coverTranslate}%)`,
              transition: dragging ? "none" : undefined,
            }}
            aria-hidden
          >
            <div className={styles.coverInner}>
              <span className={styles.coverSeal}>
                <LockOpen size={17} />
              </span>
              <span className={styles.coverKicker}>
                Kept safe while you were away
              </span>
              <span className={styles.coverBig}>Everything's still here</span>
            </div>
            <div
              className={`${styles.handle} ${hinted ? "" : styles.handlePulse}`}
            >
              <ArrowUp size={14} />
              <span>Drag up</span>
            </div>
          </div>

          {/* settles in once revealed */}
          <div
            className={`${styles.revealTag} ${revealed ? styles.revealTagOn : ""}`}
            aria-live="polite"
          >
            <span className={styles.revealTick} aria-hidden>
              <Tick size={11} />
            </span>
            <b>All here — nothing missing.</b>
            <span className={styles.revealNote}>Sample library shown</span>
          </div>
        </div>

        {!revealed ? (
          <button className={styles.liftBtn} onClick={liftCover}>
            <ArrowUp size={15} /> Lift the cover
          </button>
        ) : (
          <p className={styles.stageCaption}>
            Turning Premium back on simply resumes automatic protection —
            starting from today.
          </p>
        )}
      </section>

      {/* ── honest reassurance, made the spine (not a footnote) ───────────── */}
      <ul className={styles.truths}>
        {[
          "Nothing was deleted, moved, or downgraded while you were away",
          "Your library stayed private and encrypted on your device",
          "Resume anytime — you pick up right where you left off",
        ].map((t) => (
          <li key={t}>
            <span className={styles.truthDot} aria-hidden>
              <Tick size={10} />
            </span>
            {t}
          </li>
        ))}
      </ul>

      {/* what switches back on when you return — real Premium bundle, present-tense */}
      <div className={styles.backOn}>
        <span className={styles.backOnLabel}>
          Back on the moment you return
        </span>
        <div className={styles.backOnRow}>
          <span className={styles.backOnItem}>
            <i>
              <CloudSync size={16} />
            </i>
            Automatic full-resolution backup
          </span>
          <span className={styles.backOnItem}>
            <i>
              <Devices size={16} />
            </i>
            In sync across every device
          </span>
          <span className={styles.backOnItem}>
            <i>
              <Shield size={16} />
            </i>
            Private, encrypted, only yours
          </span>
        </div>
      </div>

      {/* ── plans — yearly featured, monthly small & secondary ─────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${
            plan === "yearly" ? styles.planSel : ""
          }`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.planTop}>
            <span className={styles.planName}>Yearly</span>
            {savingsPct > 0 && (
              <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
            )}
          </span>
          <span className={styles.planNote}>
            Set it once and protection stays on all year — no monthly
            re-thinking
          </span>
          <span className={styles.planPriceRow}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/year</span>
            <span className={styles.planSmall}>≈ {yearlyPerMonth}/mo</span>
          </span>
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.planMonthSel : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>{ctaLabel}</span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        Welcome back. Your memories never went anywhere — turning Premium back
        on just resumes the automatic protection around them.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default HomecomingPaywall
